import React from "react";
import { TRoadblockAwaitableEntity } from "../../../../../domain/codecs/form/RoadblockForm";
import { TPlainUser } from "../../../../../domain/codecs/User";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Background } from "../../BuildingBlocks/Background";
import { CRMUserAvatar } from "../../Complex/CRMUserAvatar/CRMUserAvatar";
import { CRMAssignUserAvatar } from "../../Complex/CRMAssignUserAvatar/CRMAssignUserAvatar";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../WeightBold/WeightBold";
import { TRoadblockListView } from "../../../models/RoadblockListView";
import { pipe } from "fp-ts/lib/pipeable";
import { getDateDifferenceAndFormat } from "../../../../../shared/src/utilsByDomain/dateTime/getDateDifferenceAndFormat";
import { getDaysLeftUntilEndDate } from "../../../../../shared/src/utilsByDomain/dateTime/getDaysLeftUntilEndDate";
import { dateToDayAndShortMonth } from "../../../../../shared/src/utilsByDomain/dateTime/commonDateFormats";
import { CRMColors } from "../../../models/CRMColors";
import { WeightMedium } from "../../WeightMedium/WeightMedium";
import { CRMRoadblockAwaitAvatar } from "../CRMRoadblockAwaitAvatar/CRMRoadblockAwaitAvatar";

type TRoadblockTableListItemProps = {
    item: TRoadblockListView;
    assignableUsers: Array<TPlainUser>;
    awaitableEntities: Array<TRoadblockAwaitableEntity>;
    onClick: (roadblockId: string) => void;
    onAssign: (payload: { roadblock_id: string, assigned_to_user_id: string }) => void;
    onAwait: (payload: { roadblock_id: string, awaitable_entities_id: string, days_override: number | null }) => void;
    onCancelAwait: (payload: { roadblock_id: string }) => void;
};

export const CRMRoadblockTableListItem = (props: React.PropsWithChildren<TRoadblockTableListItemProps>): JSX.Element => {

    const [isAwaitPopoutOpen, setIsAwaitPopoutOpen] = React.useState(false);
    const [isAssignPopoutOpen, setIsAssignPopoutOpen] = React.useState(false);
    const HasPopoutOpen = isAwaitPopoutOpen || isAssignPopoutOpen;

    const IsOpenStatus = props.item.status === "open";
    const IsBlockedStatus = props.item.status === "blocked";
    const HasAwaitAvatar = IsOpenStatus || IsBlockedStatus;
    const ActiveAwait =
        IsBlockedStatus && props.item.blocked_till !== null
            ? {
                waiting_on: props.item.blocked_waiting_on_is_internal
                    ? "Parked"
                    : props.item.blocked_waiting_on ?? "external party",
                blocked_till: props.item.blocked_till,
                is_internal: props.item.blocked_waiting_on_is_internal,
            }
            : null;
    const ListItemName = props.item.display_name;
    const DaysTilSla = props.item.opened_on !== null
        ? getDaysLeftUntilEndDate(props.item.opened_on, props.item.sla)
        : null;
    const HighlightColor =
        IsOpenStatus && DaysTilSla !== null && DaysTilSla < 0 ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_10
        : IsOpenStatus && DaysTilSla !== null && DaysTilSla < 3 ? CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_8
        : CRMColors.NEUTRAL_PAPER
    ;
    const DaysCompletedIn = props.item.last_closed_on !== null && props.item.opened_on !== null ?
        pipe(
            props.item.opened_on,
            getDateDifferenceAndFormat(
                props.item.last_closed_on,
                (count, unit) => `Done in ${count}${unit[0]}`
            ),
        )
        : null
    ;
    const CompletedOnDate = props.item.last_closed_on
        ? dateToDayAndShortMonth(props.item.last_closed_on)
        : null
    ;

    return (
        <div
            className={`crm-roadblock-table-list-item ${HasPopoutOpen ? "crm-roadblock-table-list-item--popout-open" : ""}`}
            onClick={() => props.onClick(props.item.id)}
        >
            <SpacingRow
                justifyContent="space-between"
                spacing={CRMSpacing.LARGE}
                childSize={HasAwaitAvatar ? "1fr 36px 36px" : "1fr 36px"}
            >
                {/* TEXT */}
                <Background
                    width="100%"
                    color={HighlightColor}
                >
                    <SpacingRow
                        justifyContent="space-between"
                        spacing={CRMSpacing.MEDIUM}
                    >
                        <CRMParagraph>
                            <WeightBold>
                                {ListItemName}
                            </WeightBold>
                        </CRMParagraph>

                        {DaysCompletedIn !== null &&
                            <CRMParagraph>
                                <WeightMedium>{DaysCompletedIn} · {CompletedOnDate}</WeightMedium>
                            </CRMParagraph>
                        }

                        {DaysCompletedIn === null && ActiveAwait !== null &&
                            <CRMParagraph>
                                <WeightMedium>
                                    {props.item.blocked_waiting_on_is_internal
                                        ? ActiveAwait.waiting_on
                                        : `Chase: ${ActiveAwait.waiting_on}`
                                    } · {dateToDayAndShortMonth(ActiveAwait.blocked_till)}
                                </WeightMedium>
                            </CRMParagraph>
                        }

                        {DaysCompletedIn === null && ActiveAwait === null && DaysTilSla !== null &&
                            <CRMParagraph>
                                {DaysTilSla === 0 &&
                                    <WeightMedium>Today</WeightMedium>
                                }
                                {DaysTilSla > 0 &&
                                    <WeightMedium>{DaysTilSla}d left</WeightMedium>
                                }

                                {DaysTilSla < 0 &&
                                    <WeightMedium>{Math.abs(DaysTilSla)}d late</WeightMedium>
                                }
                            </CRMParagraph>
                        }
                    </SpacingRow>
                </Background>

                {/* AWAIT */}
                {HasAwaitAvatar &&
                    <div onClick={(e) => e.stopPropagation()}>
                        <CRMRoadblockAwaitAvatar
                            size="regular"
                            entities={props.awaitableEntities}
                            activeAwait={ActiveAwait}
                            onToggleOpen={setIsAwaitPopoutOpen}
                            onAwait={(awaitable_entities_id, days_override) => props.onAwait({
                                roadblock_id: props.item.id,
                                awaitable_entities_id,
                                days_override,
                            })}
                            onCancelAwait={() => props.onCancelAwait({
                                roadblock_id: props.item.id,
                            })}
                        />
                    </div>
                }

                {/* AVATAR */}
                {IsOpenStatus &&
                    <div onClick={(e) => e.stopPropagation()}>
                        <CRMAssignUserAvatar
                            size="regular"
                            users={props.assignableUsers}
                            onToggleOpen={setIsAssignPopoutOpen}
                            value={props.item.assigned_user?.id ?? null}
                            onChange={(assigned_to_user_id) => props.onAssign({
                                roadblock_id: props.item.id,
                                assigned_to_user_id,
                            })}
                        />
                    </div>
                }

                {/* A closed roadblock shows who owned it. A listing roadblock can genuinely
                    have had nobody, in which case the slot stays empty rather than the row
                    inventing an owner. */}
                {!IsOpenStatus && props.item.assigned_user !== undefined &&
                    <CRMUserAvatar
                        user={props.item.assigned_user}
                    />
                }
            </SpacingRow>
        </div>
    );
};
